import { z } from "zod";

import type { PoiCategory } from "@mountayaapp/types/api/data";

/**
 * POI_GROUP_IDS is the readonly tuple of the seven POI group ids, in menu order.
 * It is the single source of truth for the id vocabulary — `z.enum` consumes it
 * (so the bus tool inputs reject an unknown group), `PoiGroupId` is derived from
 * it, and POI_GROUPS pins each entry's id to one of these literals. Declared
 * `as const` so the members stay a literal-string tuple rather than widening to
 * `string[]`, which keeps `z.enum` and the derived union exact.
 */
export const POI_GROUP_IDS = [
  "water",
  "toilets",
  "parking",
  "health",
  "lodging",
  "food",
  "viewpoints",
] as const;

/**
 * PoiGroupId is one POI group's id, the union of the seven literal ids derived
 * from POI_GROUP_IDS. It is the type every store action and tool input speaks
 * for a single group.
 */
export type PoiGroupId = (typeof POI_GROUP_IDS)[number];

/**
 * poiGroupSchema is one toggleable POI group in the studio's POI overlay menu: a
 * stable `id` the store and the bus speak, the manifest `layerKeys` the group
 * controls (each a TileLayer `key` from the /v1/layers manifest — a single
 * concept like "parking" may be drawn by several render-layers sharing one key),
 * a representative `category` whose tile-overlay icon + color the UI reads to
 * label the group, and an i18n `labelKey` the control localizes its name from.
 * Zod-first because the group set is the single source of truth shared across
 * the store seed, the bus tool inputs, and the rendering hook — validating it at
 * module load proves every member is well-formed before any of those read it.
 *
 * The `category` is a PoiCategory (imported from @mountayaapp/types/api/data,
 * the same boundary the map package's POI category map reads) rather than the
 * map package's PoiCategoryStyle: core is a leaf and cannot depend on the map
 * package, so icon/color resolution happens later in the UI layer by looking the
 * category up against the canonical tile-overlay contract.
 */
export const poiGroupSchema = z.object({
  category: z.string(),
  id: z.enum(POI_GROUP_IDS),
  labelKey: z.string(),
  layerKeys: z.array(z.string()).min(1),
});

/**
 * PoiGroup is one toggleable POI group, derived from poiGroupSchema. The
 * `category` is typed as the PoiCategory enum at the const declaration (the
 * schema keeps it a plain string because core is a leaf), so a member naming a
 * category outside the generated enum is a compile error.
 */
export type PoiGroup = Omit<z.infer<typeof poiGroupSchema>, "category"> & {
  category: PoiCategory;
};

const poiGroupEntries: readonly PoiGroup[] = [
  {
    category: "WATER_SOURCE",
    id: "water",
    labelKey: "controls.pois.groups.water",
    layerKeys: ["drinking_water"],
  },
  {
    category: "TOILETS",
    id: "toilets",
    labelKey: "controls.pois.groups.toilets",
    layerKeys: ["toilets"],
  },
  {
    category: "PARKING",
    id: "parking",
    labelKey: "controls.pois.groups.parking",
    layerKeys: ["parking"],
  },
  {
    category: "HOSPITAL",
    id: "health",
    labelKey: "controls.pois.groups.health",
    layerKeys: ["hospital", "pharmacy"],
  },
  {
    category: "HUT",
    id: "lodging",
    labelKey: "controls.pois.groups.lodging",
    layerKeys: ["refuges_huts", "accommodation_hotels", "campsite", "caravan_site"],
  },
  {
    category: "RESTAURANT",
    id: "food",
    labelKey: "controls.pois.groups.food",
    layerKeys: ["restaurants_cafes"],
  },
  {
    category: "VIEWPOINT",
    id: "viewpoints",
    labelKey: "controls.pois.groups.viewpoints",
    layerKeys: ["viewpoints"],
  },
];

/**
 * POI_GROUPS is the ordered set of POI groups the studio's POI overlay menu
 * renders, top to bottom. Each group bundles the manifest layer keys that depict
 * one user-facing concept (lodging gathers refuges, hotels, campsites, and
 * caravan sites under one toggle) and names the representative POI category the
 * UI reads its icon + color from. The order is the menu order. It is the single
 * source of truth for the group set: the store seed, the bus tool input enum,
 * and the rendering hook all derive from it, so a new group is a one-entry edit
 * here (plus its id in POI_GROUP_IDS). Validated through poiGroupSchema at module
 * load so a malformed member fails fast.
 */
export const POI_GROUPS: readonly PoiGroup[] = poiGroupEntries.map((group) => {
  // Validate each member at module load so a malformed entry fails fast; the
  // entries const is already typed PoiGroup (so a bad category or id is a
  // compile error at the literal), and this parse is the runtime tripwire that
  // mirrors the static type.
  poiGroupSchema.parse(group);

  return group;
});

/**
 * DEFAULT_ENABLED_POI_GROUP_IDS are the groups enabled on a fresh studio load —
 * the critical-info subset (water, toilets, parking) a hiker most often wants on
 * the map without opting in. The remaining groups (health, lodging, food,
 * viewpoints) start hidden so the default map is not crowded. Held as a fixed
 * array so the store seed reads one source of truth rather than re-listing the
 * ids.
 */
export const DEFAULT_ENABLED_POI_GROUP_IDS: readonly PoiGroupId[] = ["water", "toilets", "parking"];

/**
 * POI_GROUP_COUNT is the total number of POI groups, derived from POI_GROUPS so
 * the pick-from-POI "some but not all enabled" check reads the live count rather
 * than a magic number.
 */
export const POI_GROUP_COUNT = POI_GROUPS.length;
